import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomFile from '../../../components/common/CustomFile'
import CustomCheckBox from '../../../components/common/CustomCheckBox'
import * as Yup from 'yup'

// Validation

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Product name is required'),
  series: Yup.string().trim().required('series is Required'),
  size: Yup.string().trim().required('Product size is required'),
  finish: Yup.string().trim().required('Product finish is required'),
  cover_photo : Yup.string().required('Product Cover Photo is required'),
  product_preview_image : Yup.string().required('product 3D/2D image is required'),
})

const bodyTypeOptions = [
  { label: "Ceramic Tiles", value: "Ceramic Tiles" },
  { label: "Porcelain Tiles", value: "Porcelain Tiles" },
  { label: "Vitrified Tiles", value: "Vitrified Tiles" },
  { label: "Color Body Tiles", value: "Color Body Tiles" },
  { label: "PGVT", value: "PGVT" },
  { label: "Mosaic Tiles", value: "Mosaic Tiles" },
  { label: "Terracotta Tiles", value: "Terracotta Tiles" },
  { label: "Metallic Tiles", value: "Metallic Tiles" },
  { label: "Glass Tiles", value: "Glass Tiles" },
  { label: "Natural Stone Tiles", value: "Natural Stone Tiles" },
  { label: "Anti-Static Tiles", value: "Anti-Static Tiles" }
];

const thicknessOptions = [
  { label: "5mm", value: "5mm" },
  { label: "6mm", value: "6mm" },
  { label: "9mm", value: "9mm" },
  { label: "15mm", value: "15mm" }
];

const tagsOptions = [
  { label: "Discounted Tiles", value: "Discounted Tiles" },
  { label: "On Sale Tiles", value: "On Sale Tiles" },
  { label: "Clearance Tiles", value: "Clearance Tiles" },
  { label: "Best Deals on Tiles", value: "Best Deals on Tiles" },
  { label: "Seasonal Offers", value: "Seasonal Offers" },
  { label: "Premium Tiles", value: "Premium Tiles" },
  { label: "Designer Tiles", value: "Designer Tiles" },
  { label: "Luxury Tiles", value: "Luxury Tiles" },
  { label: "High-End Tiles", value: "High-End Tiles" },
  { label: "Exclusive Collection", value: "Exclusive Collection" }
];

const Add = () => {
  const { id } = useParams()

  const [sizeOptions, setSizeOptions] = useState([])
  const [finishOptions, setFinishOptions] = useState([])
  const [seriesOptions, setSeriesOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()

  const fetchSizeData = async () => {
    try {
      const response = await axiosAuthInstance.get('size/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.size,
        }))
        setSizeOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchSeriesData = async () => {
    try {
      const response = await axiosAuthInstance.get(`series/dropdown/${id}`)
      if (response.status === 200) {
        setSeriesOptions(
          response.data.result.map((item) => ({
            value: item._id,
            label: item.name,
          })),
        )
      }
    } catch (error) {
      console.error('Error fetching series:', error)
    }
  }

  const fetchFinishData = async () => {
    try {
      const response = await axiosAuthInstance.get('material/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setFinishOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchSizeData()
    fetchFinishData()
    fetchSeriesData()
  }, [])

  const handleSubmit = async (values) => {
    setIsLoading(true)
    try {
      const formData = new FormData()

      Object.keys(values).forEach((key) => {
        if (
          key !== 'cover_photo' &&
          key !== 'randoms' &&
          key !== 'product_preview_image' &&
          key !== 'separator' &&
          key !== 'light' &&
          key !== 'dark' &&
          key !== 'mobileSurface' &&
          key !== 'hl'
        ) {
          formData.append(key, values[key])
        }
      })

      // Handle single file fields

      if (values.cover_photo) {
        formData.append('cover_photo', values.cover_photo)
      }

      if (values?.randoms && values.randoms.length > 0) {
        values.randoms.forEach((file, index) => {
          formData.append(`randoms`, file)
        })
      }

      if (values.product_preview_image) {
        formData.append('product_preview_image', values.product_preview_image)
      }

      if (values.separator) {
        formData.append('separator', values.separator)
      }

      if (values.light) {
        formData.append('light', values.light)
      }

      if (values.dark) {
        formData.append('dark', values.dark)
      }

      if (values.mobileSurface) {
        formData.append('mobileSurface', values.mobileSurface)
      }

      if (values.hl) {
        formData.append('hl', values.hl)
      }

      const response = await axiosAuthInstance.post('product/add', formData)

      if (response.status === 200) {
        navigate(`/company-branding/products/${id}`)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }
  const formik = useFormik({
    initialValues: {
      company: id,
      name: '',
      size: '',
      finish: '',
      series: '',
      thickness: '',
      isBorder: false,
      borderColor: '',
      isBorderShadow: false,
      borderShadowColor: '',
      isActive: false,
      bodyType: '',
      cover_photo: null,
      randoms: null,
      product_preview_image: null,
      separator: null,
      light: null,
      dark: null,
      mobileSurface: null,
      hl: null
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form className='flex flex-col p-4 gap-4' onSubmit={formik.handleSubmit}>
        <div>
          <label className='bg-blue-100 text-orange-500 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-500 dark:text-white'>
            Add Product
          </label>
        </div>

        <>
          <div className='grid grid-cols-2  gap-4'>
            <Field
              name='size'
              label='Select Size'
              placeholder='Select Size'
              required
              component={CustomSelect}
              options={sizeOptions}
            />

            <Field
              name='finish'
              label='Select Finish'
              placeholder='Select Finish'
              required
              component={CustomSelect}
              options={finishOptions}
            />

            <Field
              name='series'
              label='Select Series'
              placeholder='Select Series'
              required
              component={CustomSelect}
              options={seriesOptions}
            />

            <Field
              name='name'
              label='Product Name'
              placeholder='Product Name'
              required
              component={CustomInput}
            />

            <Field
              name='thickness'
              label='Thickness'
              placeholder='Thickness (4 mm)'
              component={CustomSelect}
              options={thicknessOptions}
            />

            <div className='mt-[30px] flex gap-4'>
              <Field
                name='isBorder'
                label='Is Border'
                className='w-24'
                component={CustomCheckBox}
              />

              <Field
                name='isBorderShadow'
                label='Is Border Shadow'
                className="w-48"
                component={CustomCheckBox}
              />

              <Field
                name='isActive'
                label='Is Active'
                component={CustomCheckBox}
              />
            </div>

            <Field
              name='borderColor'
              label='Border Color'
              placeholder='Border Color (#FF0000)'
              component={CustomInput}
            />

            <Field
              name='borderShadowColor'
              label='Border Shadow Color '
              placeholder='Border Shadow Color (#FF0000)'
              component={CustomInput}
            />

            <Field
              name='bodyType'
              label="Body Type"
              placeholder="Body Type"
              component={CustomSelect}
              options={bodyTypeOptions}
            />

            <Field
              name='tags'
              label="Tags"
              placeholder="Tags"
              component={CustomSelect}
              options={tagsOptions}
            />

            <Field
              name='cover_photo'
              accept='image/*'
              label='Product Cover Photo'
              required
              component={CustomFile}
            />

            <Field
              name='randoms'
              accept='image/*'
              label='Product Random (Select Multiple Images)'
              required
              component={CustomFile}
              multiple={true}
            />

            <Field
              name='product_preview_image'
              accept='image/*'
              label='Product 3D/2D Image'
              required
              component={CustomFile}
            />

            <Field
              name='separator'
              accept='image/*'
              label='Separator'
              component={CustomFile}
            />

            <Field
              name='light'
              accept='image/*'
              label='Light'
              component={CustomFile}
            />

            <Field
              name='dark'
              accept='image/*'
              label='Dark'
              component={CustomFile}
            />

            <Field
              name='mobileSurface'
              accept='image/*'
              label='Mobile Surface'
              component={CustomFile}
            />

            <Field
              name='hl'
              accept='image/*'
              label='HL'
              component={CustomFile}
            />
          </div>
        </>

        <div className='flex justify-end'>
          <button
            type='submit'
            disabled={isLoading}
            className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-gray-500 rounded-lg px-6 py-2'
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </FormikProvider>
  )
}

export default Add
